/**
 * SRV-F1-3: Category Hourly Rate Config Page
 * Admin settings — óradíj konfiguráció gépkategóriánként
 */

import {
  deleteHourlyRateConfig,
  getHourlyRateConfigs,
  getMachineCategories,
  type HourlyRateConfig,
  type MachineCategory,
  upsertHourlyRateConfig,
} from '@/api/worksheets';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useApiError } from '@/hooks/use-api-error';
import { useTranslation } from '@kgc/i18n';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const FALLBACK_RATES: Record<string, number> = {
  ELECTRIC_SMALL: 8000,
  ELECTRIC_LARGE: 10000,
  PETROL_HANDHELD: 10000,
  PETROL_PUSH: 12000,
  TRACTOR: 15000,
  BATTERY: 8000,
};

const formatCurrency = (amount: number): string =>
  new Intl.NumberFormat('hu-HU', {
    style: 'currency',
    currency: 'HUF',
    maximumFractionDigits: 0,
  }).format(amount);

export function CategoryHourlyRateConfigPage() {
  const { t } = useTranslation();
  const { getErrorMessage } = useApiError();
  const navigate = useNavigate();
  const [categories, setCategories] = useState<MachineCategory[]>([]);
  const [configs, setConfigs] = useState<HourlyRateConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [catRes, rateRes] = await Promise.all([getMachineCategories(), getHourlyRateConfigs()]);
      setCategories(catRes.data);
      setConfigs(rateRes.data);

      // Initialize edit values from existing configs
      const values: Record<string, string> = {};
      for (const c of catRes.data) {
        const existing = rateRes.data.find(r => r.machineCategoryId === c.id);
        values[c.id] = existing ? String(existing.hourlyRateNet) : '';
      }
      setEditValues(values);
    } catch {
      setError(t('common.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const handleSave = async (categoryId: string) => {
    const value = Number(editValues[categoryId]);
    if (isNaN(value) || value < 0) {
      setError(t('worksheet.hourlyRateConfig.invalidRate'));
      return;
    }
    setSaving(categoryId);
    setError(null);
    try {
      await upsertHourlyRateConfig({
        machineCategoryId: categoryId,
        hourlyRateNet: value,
      });
      await loadData();
    } catch (err) {
      setError(getErrorMessage(err, t('common.error')));
    } finally {
      setSaving(null);
    }
  };

  const handleDelete = async (configId: string) => {
    setSaving(configId);
    try {
      await deleteHourlyRateConfig(configId);
      await loadData();
    } catch (err) {
      setError(getErrorMessage(err, t('common.error')));
    } finally {
      setSaving(null);
    }
  };

  const getConfigForCategory = (categoryId: string): HourlyRateConfig | undefined =>
    configs.find(c => c.machineCategoryId === categoryId);

  if (loading) {
    return (
      <div className="min-h-screen kgc-bg p-6">
        <div className="max-w-4xl mx-auto">
          <Card>
            <CardContent className="p-12 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4" />
              <p className="text-muted-foreground">{t('common.loading')}</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen kgc-bg">
      <div className="bg-white dark:bg-[#1a4f5c] border-b border-gray-200 dark:border-[#3a9aa8] px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => navigate('/settings')}>
              {t('common.back')}
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                {t('worksheet.hourlyRateConfig.title')}
              </h1>
              <p className="text-sm text-muted-foreground">
                {t('worksheet.hourlyRateConfig.subtitle')}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-6">
        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-950/20 border border-red-300 dark:border-red-600 text-red-700 dark:text-red-300 text-sm">
            {error}
          </div>
        )}

        <Card>
          <CardHeader>
            <CardTitle>{t('worksheet.hourlyRateConfig.tableTitle')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground">
                      {t('worksheet.hourlyRateConfig.category')}
                    </th>
                    <th className="text-right py-3 px-4 font-medium text-muted-foreground">
                      {t('worksheet.hourlyRateConfig.fallbackRate')}
                    </th>
                    <th className="text-right py-3 px-4 font-medium text-muted-foreground">
                      {t('worksheet.hourlyRateConfig.configuredRate')}
                    </th>
                    <th className="text-center py-3 px-4 font-medium text-muted-foreground">
                      {t('common.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {categories.map(cat => {
                    const config = getConfigForCategory(cat.id);
                    const fallback = FALLBACK_RATES[cat.code] ?? 8000;
                    const isSaving = saving === cat.id || saving === config?.id;

                    return (
                      <tr
                        key={cat.id}
                        className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-900/20"
                      >
                        <td className="py-3 px-4">
                          <div className="font-medium">{cat.name}</div>
                          <div className="text-xs text-muted-foreground">{cat.code}</div>
                        </td>
                        <td className="py-3 px-4 text-right text-muted-foreground">
                          {formatCurrency(fallback)}
                        </td>
                        <td className="py-3 px-4 text-right">
                          <Input
                            type="number"
                            min="0"
                            step="500"
                            className="w-32 text-right ml-auto"
                            placeholder={String(fallback)}
                            value={editValues[cat.id] ?? ''}
                            onChange={e =>
                              setEditValues(prev => ({ ...prev, [cat.id]: e.target.value }))
                            }
                          />
                        </td>
                        <td className="py-3 px-4 text-center">
                          <div className="flex gap-1 justify-center">
                            <Button
                              size="sm"
                              disabled={isSaving || !editValues[cat.id]}
                              onClick={() => handleSave(cat.id)}
                            >
                              {isSaving ? '...' : t('common.save')}
                            </Button>
                            {config && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600 dark:text-red-400"
                                disabled={isSaving}
                                onClick={() => handleDelete(config.id)}
                              >
                                {t('common.delete')}
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="mt-4 p-3 rounded bg-blue-50 dark:bg-blue-950/20 text-sm text-blue-700 dark:text-blue-300">
              {t('worksheet.hourlyRateConfig.priorityInfo')}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
